import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useRelationship } from 'services/repositories/relationships/relationships';
import { SUPPORT_EMAIL } from 'support/helpers/const/const';
import { getTradePartner } from 'support/helpers/relationships/relationships';
import { useConnectionNavigation } from 'pages/Connections/ConnectionDetailsPage/contexts/ConnectionNavigationContext';
import { SlideOver, SlideOverSubtitleSkeleton, SlideOverTitleSkeleton } from 'components/Display/SlideOver/SlideOver';
import { PartnerLogo } from 'components/PartnerLogo/PartnerLogo';
import { Translate } from 'components/Translate/Translate';

export const GoLiveSlideOver = ({ open }: { open: boolean }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { partnerId, relationshipId } = useParams<{ partnerId: string; relationshipId: string }>();
  const { routeTo } = useConnectionNavigation();

  const { data: currentPartner, isLoading: isCurrentPartnerLoading } = useCurrentPartner();
  const { data: relationship, isLoading: isLoadingRelationship } = useRelationship({
    variables: { relationshipId },
    keepPreviousData: true,
    enabled: Boolean(relationshipId),
  });

  const isLoading = isCurrentPartnerLoading || isLoadingRelationship;
  const otherPartner = relationship && currentPartner && getTradePartner(relationship, currentPartner.id);
  const isSender = relationship?.sender_partner?.id === currentPartner?.id;

  useEffect(() => {
    if (open && !isLoading && !isSender && relationshipId) {
      navigate(routeTo('relationshipDetails', { partnerId: partnerId!, relationshipId: relationshipId! }), {
        replace: true,
      });
    }
  }, [open, isLoading, isSender, navigate, partnerId, relationshipId, routeTo]);

  return (
    <SlideOver
      size="large"
      open={open}
      setOpen={() =>
        navigate(
          routeTo('relationshipDetails', {
            partnerId: partnerId!,
            relationshipId: relationshipId!,
          }),
        )
      }
      title={isLoading ? <SlideOverTitleSkeleton /> : otherPartner?.name}
      subtitle={isLoading ? <SlideOverSubtitleSkeleton /> : t('connections:goLiveSlideOver.subtitle')}
      headerImage={<PartnerLogo imageUrl={otherPartner?.logo_url} size="large" isLoading={isLoading} />}
    >
      <div className="space-y-8">
        <div className="space-y-1 opacity-80">
          <h3 className="text-base font-semibold leading-6 text-gray-900">
            {t('connections:goLiveSlideOver.activateConnection.title')}
          </h3>
          <p className="text-sm leading-5 text-gray-700">
            <Translate i18nKey="connections:goLiveSlideOver.activateConnection.description" />
          </p>
        </div>
        <div className="border-t border-gray-200" />
        <p className="text-sm leading-5 text-gray-500">
          <Translate
            i18nKey="connections:goLiveSlideOver.support"
            components={{
              link: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gray-900 underline" />,
            }}
          />
        </p>
      </div>
    </SlideOver>
  );
};
